import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Button } from "@/components/ui/Button";
import { ErrorAlert } from "@/components/ui/ErrorAlert";
import { SettingsSection } from "@/components/SettingsSection";
import { SettingsRow } from "@/components/SettingsRow";
import {
  useBillingStatusQuery,
  useCheckoutSessionMutation,
  usePortalSessionMutation,
  useResyncBillingMutation,
} from "@/features/billing/hooks/useBilling";
import type { BillingStatus, SubscriptionProduct } from "@/features/billing/types";
import { extractMessage } from "@/lib/errors";

function subscriptionStatusLabel(status: string, t: TFunction): string {
  switch (status) {
    case "active":
      return t("billing.status.active");
    case "trialing":
      return t("billing.status.trialing");
    case "past_due":
      return t("billing.status.past_due");
    case "canceled":
      return t("billing.status.canceled");
    case "unpaid":
      return t("billing.status.unpaid");
    case "incomplete":
      return t("billing.status.incomplete");
    case "incomplete_expired":
      return t("billing.status.incomplete_expired");
    default:
      return status;
  }
}

function subscriptionDescription(status: BillingStatus, t: TFunction, locale: string): string {
  if (!status.subscriptionStatus) return t("billing.noActiveSubscription");
  const label = subscriptionStatusLabel(status.subscriptionStatus, t);
  if (!status.currentPeriodEnd) return label;
  const date = new Date(status.currentPeriodEnd).toLocaleDateString(locale);
  return t("billing.nextDueDate", { label, date });
}

export function SubscriptionSection() {
  const { t, i18n } = useTranslation();
  const { data: status, isLoading } = useBillingStatusQuery();
  const checkout = useCheckoutSessionMutation();
  const portal = usePortalSessionMutation();
  const resync = useResyncBillingMutation();
  const [error, setError] = useState<string | null>(null);
  const [resyncMessage, setResyncMessage] = useState<string | null>(null);

  async function handleUpgrade(product: SubscriptionProduct) {
    setError(null);
    try {
      await checkout.mutateAsync(product);
    } catch (err) {
      setError(extractMessage(err));
    }
  }

  async function handleManage() {
    setError(null);
    try {
      await portal.mutateAsync();
    } catch (err) {
      setError(extractMessage(err));
    }
  }

  async function handleResync() {
    setError(null);
    setResyncMessage(null);
    try {
      const result = await resync.mutateAsync();
      setResyncMessage(
        result.plan === "PAID" ? t("billing.resyncFound") : t("billing.resyncNotFound"),
      );
    } catch (err) {
      setError(extractMessage(err));
    }
  }

  if (isLoading || !status) {
    return (
      <SettingsSection title={t("billing.yourSubscription")}>
        <div className="px-4 py-3 text-sm text-muted-foreground">{t("common.loading")}</div>
      </SettingsSection>
    );
  }

  return (
    <SettingsSection title={t("billing.yourSubscription")}>
      <SettingsRow
        label={t("billing.subscriptionLabel")}
        description={subscriptionDescription(status, t, i18n.language)}
      >
        <div className="flex flex-col items-end gap-2">
          {status.hasStripeCustomer ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={handleManage}
              disabled={portal.isPending}
            >
              {portal.isPending ? t("billing.redirecting") : t("billing.manage")}
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => handleUpgrade("SELF_HOSTED_PREMIUM")}
                disabled={checkout.isPending}
              >
                {checkout.isPending ? t("billing.redirecting") : t("billing.selfHostedPremium")}
              </Button>
              <Button
                size="sm"
                onClick={() => handleUpgrade("CLOUD")}
                disabled={checkout.isPending}
              >
                {checkout.isPending ? t("billing.redirecting") : t("billing.cloud")}
              </Button>
            </div>
          )}
          {status.plan === "FREE" && (
            <Button size="sm" variant="ghost" onClick={handleResync} disabled={resync.isPending}>
              {resync.isPending ? t("billing.resyncing") : t("billing.resync")}
            </Button>
          )}
          {resyncMessage && <p className="text-sm text-muted-foreground">{resyncMessage}</p>}
          {error && <ErrorAlert message={error} />}
        </div>
      </SettingsRow>
    </SettingsSection>
  );
}
